import { Notification } from '@app/@types/redux/users';
import NotificationModal from '@components/Projects/TakeProject/Elements/NotificationModal';
import env from '@config/env';
import { i18nCText } from '@libs/i18n/I18n';
import logger from '@libs/log';
import { updateNotifications as updateNotificationsFn } from '@state/redux/slices/user.slice';
import { useAppDispatch } from '@state/redux/store';
import classnames from 'classnames';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaBan, FaBell, FaChevronUp, FaExclamation } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import tedoIcon from '../../../assets/icons/tedo.png';
import Api from '../../../state/utils/Api';
import { translate } from '../../../state/utils/helper';
import {
  Badge,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  ListGroup,
  ListGroupItem,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from '../Html';

function getIcon(event: string) {
  if (event === 'level_up') {
    return FaChevronUp;
  }
  if (event === 'profanity') {
    return FaExclamation;
  }
  if (event === 'plagiarism') {
    return FaBan;
  }
  return FaBell;
}

function Notifications() {
  const fetcher = async () => {
    const response = await Api.getNotifications();
    return response.data.data;
  };

  const [liveNotificationFeed, setLiveNotificationFeed] = useState(true);

  const { data: notifications = [] } = useSWR('notifications', fetcher, {
    refreshInterval: liveNotificationFeed && env.PROD ? 2000 : undefined,
  });

  const dispatch = useAppDispatch();

  async function updateNotifications(notificationIds: string[]) {
    try {
      await dispatch(updateNotificationsFn(notificationIds));
    } catch (error) {
      logger.error('Error updating notifications:');
    }
  }

  function updateReadNotifications() {
    const notificationIds = notifications.map((x) => x.id);
    updateNotifications(notificationIds).catch(() => {
      logger.error('Error updating notifications:');
    });
  }

  const [floatingNotification, setFloatingNotification] = useState(false);

  useEffect(() => {
    if (notifications.length > 0) {
      const unreadNotifications = notifications.filter((x) => !x.attributes.userChecked);
      setFloatingNotification(Boolean(unreadNotifications.length));
    }
  }, [notifications]);

  const unreadNotifications = notifications.filter((x) => !x.attributes.userChecked);

  const unreadNotificationsCount = unreadNotifications.length;

  useEffect(() => {
    if (floatingNotification) {
      setLiveNotificationFeed(false);
    } else {
      setLiveNotificationFeed(true);
    }
  }, [floatingNotification]);

  const navigate = useNavigate();
  const location = useLocation();

  function generateNotification(
    notification: Notification,
    updateNotifications?: (notificationIds: string[]) => Promise<void>,
    floating = false
  ) {
    const {
      id,
      attributes: { event, notifiableType, userChecked, createdAt, data },
    } = notification;
    const NotificationIcon = getIcon(event);
    return (
      <ListGroupItem
        key={id}
        className={classnames(
          userChecked ? 'notification-read' : `notification-unread`,
          data.link ? 'notification-item-pointer' : '',
          `list-group-item-action`,
          { 'floating-notification bg-gradient-default': floating }
        )}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          if (data.link && updateNotifications) {
            updateNotifications([id]).catch(() => {
              logger.error('Error updating notifications');
            });
            // const url = `${window.location.protocol}//${window.location.host}`;
            // window.open(`${url}/${data.link}/notification`, '_self');
            const link = `/${data.link}`;
            if (location.pathname === link) {
              window.location.reload();
            }
            navigate(link);
          }
        }}
        tag="a"
      >
        <Row className="align-items-center">
          <Col className="col-auto">
            <NotificationIcon className={classnames('text-center')} />
          </Col>
          <div className="col ml--2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0 text-sm floating-notification-title">
                  {translate(`notifications.types.${notifiableType}`)}
                </h4>
              </div>
              <div className="text-right text-muted floating-notification-time">
                <small>{moment(createdAt).fromNow()}</small>
              </div>
            </div>
            <p className="text-sm mb-0">
              {translate(`notifications.events.${event}`, {
                stage: i18nCText(data.projectStage || ''),
                project: i18nCText(data.project || 'challenge'),
              })}
            </p>
          </div>
        </Row>
      </ListGroupItem>
    );
  }

  const NotificationIcon = (notifiableType: string) => {
    if (notifiableType.toLowerCase() === 'userproject') return tedoIcon;
    else return FaBell;
  };
  return (
    <>
      {/* <CSSTransition
        in={floatingNotification}
        timeout={{ enter: 1000, exit: 300 }}
        unmountOnExit
        classNames="notification-slide"
      >
        <div className="floating-notification-container floating">
          <button
            type="button"
            onClick={() => {
              updateNotifications([notifications[0].id]).catch(() => {
                logger.error('Error updating notifications:');
              });

              setFloatingNotification(false);
            }}
          >
            <FiX />
          </button>
          <div>
            {unreadNotificationsCount &&
              generateNotification(unreadNotifications[0], updateNotifications, true)}
          </div>
        </div>
      </CSSTransition> */}
      {unreadNotifications[0] && (
        <NotificationModal
          isOpen={floatingNotification}
          toggle={() => {
            updateNotifications([notifications[0].id]).catch(() => {
              logger.error('Error updating notifications:');
            });
            setFloatingNotification(false);
          }}
          onViewMessage={() => {
            const data = unreadNotifications[0]?.attributes?.data;
            const id = unreadNotifications[0]?.id;
            if (data.link) {
              updateNotifications([id])
                .then(() => {
                  const link = `/${data.link}`;
                  if (location.pathname === link) {
                    window.location.reload();
                  }
                  window.location.assign(link);
                })
                .catch(() => {
                  logger.error('Error updating notifications');
                });
            }
            updateNotifications([notifications[0].id]).catch(() => {
              logger.error('Error updating notifications:');
            });
            setFloatingNotification(false);
          }}
          icon={NotificationIcon(unreadNotifications[0]?.attributes?.notifiableType)}
          title={translate(
            `notifications.types.${unreadNotifications[0]?.attributes?.notifiableType}`
          )}
          message={translate(`notifications.events.${unreadNotifications[0]?.attributes?.event}`, {
            stage: i18nCText(unreadNotifications[0]?.attributes?.data?.projectStage || ''),
            project: i18nCText(unreadNotifications[0]?.attributes?.data?.project || 'challenge'),
          })}
          buttonIcon={
            unreadNotifications[0]?.attributes?.notifiableType.toLowerCase() === 'userproject'
              ? 'https://cartedo.s3.us-east-2.amazonaws.com/web/f2c2b798b238782c591d.png'
              : undefined
          }
          buttonText={
            unreadNotifications[0]?.attributes?.notifiableType.toLowerCase() === 'userproject'
              ? translate('notificationModal.clickToViewFeedback')
              : undefined
          }
        />
      )}

      <UncontrolledDropdown nav inNavbar className="d-none d-md-block my-auto">
        <DropdownToggle
          nav
          onClick={() => {
            updateReadNotifications();
          }}
          id="notificationsNavbarToolTip"
        >
          <span>
            <FaBell style={{ fontSize: 20 }} />
          </span>
          {unreadNotificationsCount ? (
            <Badge
              color="danger"
              className="badge-md badge-circle badge-floating border-white notification-badge"
            >
              {unreadNotificationsCount}
            </Badge>
          ) : null}
        </DropdownToggle>
        <UncontrolledTooltip delay={0} placement="bottom" target="notificationsNavbarToolTip">
          {translate('layout.navbar.notifications')}
        </UncontrolledTooltip>
        <DropdownMenu className="dropdown-menu-xl mt-2" right>
          <div className="px-3 py-3">
            <h6 className="text-sm text-muted m-0">
              {translate('notifications.unreadPre')}{' '}
              <strong className="text-info">{unreadNotificationsCount}</strong>{' '}
              {translate('notifications.unreadPost')}
            </h6>
          </div>
          <ListGroup flush>
            {notifications
              .sort((a, b) => (a.attributes.createdAt < b.attributes.createdAt ? 1 : -1))
              .slice(0, 5)
              .map((notification) => generateNotification(notification, updateNotifications))}
          </ListGroup>
          <DropdownItem
            className="text-center text-info font-weight-bold py-3"
            to="/notifications"
            tag={Link}
          >
            {translate('notifications.viewAll')}
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </>
  );
}

export default Notifications;
